import { FC } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useDialogs } from '@toolpad/core/useDialogs';
import reactStringReplace from 'react-string-replace';
import { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import * as motion from 'motion/react-client';

import { type Order } from '@api';
import { TinPreview } from '@core/features/artworks';
import { OrderDetailDialog } from './OrderDetailDialog';

dayjs.extend(relativeTime);

type Props = {
  order: Order;
};

export const OrderListItem: FC<Props> = ({ order }) => {
  const dialogs = useDialogs();
  const { t } = useTranslation();

  const handleOpenDetail = async () => {
    await dialogs.open(OrderDetailDialog, order.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.4,
        scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 },
      }}
    >
      <Box
        onClick={handleOpenDetail}
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          alignItems: {
            xs: 'flex-start',
            sm: 'center',
          },
          justifyContent: 'space-between',
          backgroundColor: '#ECF1FC',
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: { xs: 4, md: 8 },
          paddingRight: 8,
          borderRadius: 4,
          position: 'relative',
          cursor: 'pointer',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 0, md: 6 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box
            sx={(theme: Theme) => ({
              width: 20,
              height: 20,
              borderRadius: 50,
              backgroundColor: '#00A39C',
              [theme.breakpoints.down('md')]: {
                position: 'absolute',
                top: 16,
                right: 24,
              },
            })}
          />

          {order.positions[0] && (
            <Box
              sx={(theme: Theme) => ({
                width: 60,
                height: 60,
                [theme.breakpoints.down('md')]: {
                  marginBottom: { xs: '12px !important' },
                },
              })}
            >
              <TinPreview
                size="small"
                image={order.positions[0].artwork?.thumbnail ?? ''}
              />
            </Box>
          )}
          <Box
            sx={(theme: Theme) => ({
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              [theme.breakpoints.down('md')]: {
                marginBottom: { xs: '12px !important' },
              },
            })}
          >
            <Typography
              sx={{ fontSize: 18, fontWeight: 400, textTransform: 'uppercase' }}
            >
              {reactStringReplace(order.venue, 'Nivea Haus', (match, i) => (
                <Box
                  component="b"
                  key={i}
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  {match}
                </Box>
              ))}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
              {t('orderedOn')} {dayjs(order?.created_at).format('D. MMMM YYYY')}
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained">{t('showOrder')}</Button>
      </Box>
    </motion.div>
  );
};
